import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import EventCatalogAttachSection from "./EventCatalogAttachSection";
import {
  buildEventCatalogPayload,
  snapshotIdsFromEvent,
  validateEventCatalogAttach,
} from "../../helpers/clubCatalog";
import { buildEventUpdateFromRecord } from "../../helpers/managerEventUpdate";
import { updateEvent } from "../../store/features/event/eventSlice";
import { showToast } from "../../store/features/toast/toastSlice";

const EventCatalogManagerAttach = ({ eventItem, clubId, onSaved }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const seeded = useMemo(() => snapshotIdsFromEvent(eventItem), [eventItem]);

  const { control, setValue, getValues, reset } = useForm({
    defaultValues: {
      addonItemIds: seeded.addonItemIds,
      preorderItemIds: seeded.preorderItemIds,
      addonPriceOverrides: seeded.addonPriceOverrides,
      preorderPriceOverrides: seeded.preorderPriceOverrides,
      addonStockQty: seeded.addonStockQty,
      preorderStockQty: seeded.preorderStockQty,
    },
  });

  useEffect(() => {
    reset({
      addonItemIds: seeded.addonItemIds,
      preorderItemIds: seeded.preorderItemIds,
      addonPriceOverrides: seeded.addonPriceOverrides,
      preorderPriceOverrides: seeded.preorderPriceOverrides,
      addonStockQty: seeded.addonStockQty,
      preorderStockQty: seeded.preorderStockQty,
    });
  }, [seeded, reset]);

  if (!clubId || !eventItem?._id) return null;

  const onSave = async () => {
    const values = getValues();
    const stockErr = validateEventCatalogAttach(values, eventItem, t);
    if (stockErr) {
      dispatch(
        showToast({
          show: false,
          message: stockErr,
          type: "error",
        })
      );
      return;
    }
    const catalog = buildEventCatalogPayload({
      clubId,
      addonItemIds: values.addonItemIds,
      preorderItemIds: values.preorderItemIds,
      addonPriceOverrides: values.addonPriceOverrides,
      preorderPriceOverrides: values.preorderPriceOverrides,
      addonStockQty: values.addonStockQty,
      preorderStockQty: values.preorderStockQty,
    });
    const payload = buildEventUpdateFromRecord(eventItem, (base) => ({
      ...base,
      ...catalog,
    }));
    if (!payload) return;
    setSaving(true);
    try {
      const resultAction = await dispatch(updateEvent(payload));
      if (updateEvent.fulfilled.match(resultAction)) {
        dispatch(
          showToast({
            show: false,
            message: resultAction?.payload?.message || t("CATALOG_ATTACH_SAVED"),
            type: "success",
          })
        );
        onSaved?.();
      } else {
        dispatch(
          showToast({
            show: false,
            message: resultAction?.error?.message || t("CATALOG_SAVE_ERROR"),
            type: "error",
          })
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3">
      <Alert variant="secondary" className="py-2 small">
        {t("CATALOG_MANAGER_ATTACH_HINT")}
      </Alert>
      <EventCatalogAttachSection
        control={control}
        setValue={setValue}
        clubId={clubId}
        eventItem={eventItem}
        canManageCatalog
        catalogManageHref="/club-catalog"
      />
      <Button
        type="button"
        className="mt-3"
        disabled={saving}
        onClick={onSave}
      >
        {saving ? <Spinner size="sm" animation="border" className="me-2" /> : null}
        {t("CATALOG_SAVE_EVENT_ATTACH")}
      </Button>
    </div>
  );
};

export default EventCatalogManagerAttach;
